// Active-subject switcher. V1.1 caregiver mode (focus group #15 / #23).
// Lists the profiles the account holder manages and lets them flip the
// active one. Selection persists via cookie (set by PATCH
// /api/profiles/active). When only one profile exists (self), the
// switcher renders as a non-interactive label.
//
// Round-3 fix: the V1.1 ship had a race condition where the
// CaregiverKindFirstRun prompt POSTed to /api/profiles but the
// ProfileSwitcher's useEffect only ran on mount, so the switcher
// stayed hidden behind the `profiles.length <= 1` guard even after
// the caregiver-profile creation succeeded. 4/16 round-3 personas
// flagged this. Fix: subscribe to a custom 'medreview:profiles-changed'
// window event that any code path can dispatch after creating /
// deleting / mutating a profile.
//
// The deeper sub-profile data scoping (medications, flags, etc.) lands
// in a follow-up PR; see CLAUDE-RESEARCH-GAP in
// packages/api/src/routes/profiles.ts. For V1.1 the switcher is a
// visible affordance + identity surface; the rest of the app still
// scopes by user_id under the hood.

import { useCallback, useEffect, useState } from 'react';
import { useAuthedFetch } from '../hooks/useAuthedFetch';
import { useMe } from '../hooks/useMe';

interface Profile {
  id: string;
  subjectName: string;
  isSelf: boolean;
  relationship: string | null;
  createdAt: string;
}

// Other components dispatch this after creating or deleting a profile
// (CaregiverKindFirstRun, future caregiver-onboarding flow). The
// switcher refetches whenever it fires.
export const PROFILES_CHANGED_EVENT = 'medreview:profiles-changed';

export function notifyProfilesChanged(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(PROFILES_CHANGED_EVENT));
}

export function ProfileSwitcher(): JSX.Element | null {
  const authedFetch = useAuthedFetch();
  const { me } = useMe();
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const fetchProfiles = useCallback(async () => {
    try {
      const res = await authedFetch('/api/profiles');
      if (!res.ok) return;
      const j = (await res.json()) as { ok: boolean; data?: { items: Profile[] } };
      if (j.ok && j.data) {
        setProfiles(j.data.items);
        // Default active = self if cookie hasn't set one yet.
        if (!activeId) {
          const self = j.data.items.find((p) => p.isSelf);
          if (self) setActiveId(self.id);
        }
      }
    } catch {
      // ignore
    }
  }, [authedFetch, activeId]);

  useEffect(() => {
    void fetchProfiles();
  }, [fetchProfiles]);

  // Refetch when any code path creates / deletes a profile.
  useEffect(() => {
    const handler = (): void => {
      void fetchProfiles();
    };
    window.addEventListener(PROFILES_CHANGED_EVENT, handler);
    return () => window.removeEventListener(PROFILES_CHANGED_EVENT, handler);
  }, [fetchProfiles]);

  const setActive = async (id: string) => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await authedFetch('/api/profiles/active', {
        method: 'PATCH',
        body: JSON.stringify({ profileId: id }),
      });
      if (res.ok) setActiveId(id);
    } finally {
      setBusy(false);
      setOpen(false);
    }
  };

  // V1 self-only path keeps the switcher hidden. As soon as the user
  // has declared caregiver mode in accessibility prefs, we render the
  // switcher even with only the self profile back from the API: lets
  // the user see SOMETHING during the race between profile creation
  // and the next fetch, and lets them open the menu to add another
  // subject. Without this, the round-3 regression repeats: prompt
  // says "A loved one", switcher never appears.
  const isCaregiverMode = me?.accessibility?.profileKind === 'caregiver';
  if (profiles.length <= 1 && !isCaregiverMode) return null;

  const active = profiles.find((p) => p.id === activeId) ?? profiles[0];
  if (!active) return null;

  return (
    <div style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Switch active subject. Currently ${active.subjectName}.`}
        style={{
          background: 'var(--color-surface-secondary)',
          border: '1.5px solid var(--color-border)',
          borderRadius: 'var(--radius-md)',
          padding: '6px 12px',
          fontSize: 14,
          fontWeight: 600,
          cursor: 'pointer',
          color: 'var(--color-text-primary)',
        }}
      >
        For: {active.subjectName} ▾
      </button>
      {open ? (
        <div
          role="menu"
          style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            marginTop: 4,
            minWidth: 200,
            background: 'var(--color-surface-primary)',
            border: '1.5px solid var(--color-border)',
            borderRadius: 'var(--radius-md)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            zIndex: 10,
          }}
        >
          {profiles.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => void setActive(p.id)}
              disabled={busy}
              role="menuitemradio"
              aria-checked={p.id === activeId}
              style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                padding: '10px 14px',
                background: p.id === activeId ? 'var(--color-surface-secondary)' : 'transparent',
                border: 0,
                cursor: 'pointer',
                fontSize: 15,
                color: 'var(--color-text-primary)',
              }}
            >
              <strong>{p.subjectName}</strong>
              {p.relationship ? (
                <span
                  style={{ display: 'block', fontSize: 13, color: 'var(--color-text-secondary)' }}
                >
                  {p.relationship}
                </span>
              ) : null}
              {p.isSelf ? (
                <span
                  style={{ display: 'block', fontSize: 12, color: 'var(--color-text-tertiary)' }}
                >
                  You
                </span>
              ) : null}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
